/**
 * Admin Analytics - Eigen analytics dashboard (Google Analytics-achtig)
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Download,
  Eye,
  FilePlus,
  FileCheck,
  GitCompare,
  Landmark,
  MapPin,
  PiggyBank,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';

const DAY_OPTIONS = [7, 30, 90, 180, 365] as const;

export function AdminAnalytics() {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(30);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-stats', days],
    queryFn: () => api.getAnalyticsStats({ tenant_id: 'default', days }),
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');
  const isForbidden =
    errMsg.toLowerCase().includes('beheerder') || errMsg.toLowerCase().includes('forbidden');

  const eventRows =
    data?.events_by_type &&
    Object.entries(data.events_by_type)
      .filter(([type]) => type !== 'page_view')
      .sort((a, b) => b[1] - a[1]);

  async function handleExportCsv() {
    setCsvError(null);
    setCsvBusy(true);
    try {
      const blob = await api.exportAnalyticsStatsCsv({ tenant_id: 'default', days, delimiter: ';' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calqtool-analytics-default-${days}d.csv`;
      a.rel = 'noopener';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : 'Export mislukt');
    } finally {
      setCsvBusy(false);
    }
  }

  return (
    <div>
      <SeoMeta
        path="/admin/analytics"
        noindex
        title="Analytics"
        description="First-party analytics CalqTool™: page views, conversies en toolgebruik uit eigen events — export CSV, alleen platformbeheerder."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Analytics' }]} />
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-2xl font-bold text-text">Analytics</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="analytics-days" className="text-sm text-text-muted">
            Periode
          </label>
          <select
            id="analytics-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value) as (typeof DAY_OPTIONS)[number])}
            className="rounded-lg border border-border bg-surface-card px-3 py-2 text-sm text-text"
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Laatste {d} dagen
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={csvBusy || !data}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            {csvBusy ? 'Exporteren…' : 'CSV exporteren'}
          </button>
        </div>
      </div>
      {csvError && (
        <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100" role="status">
          {csvError}
        </p>
      )}
      <p className="mb-6 text-sm text-text-muted">
        Overzicht van bezoeken, projecten en conversies. <strong className="text-text">Paginaweergaven</strong> en{' '}
        <strong className="text-text">events</strong> gelden voor de gekozen periode (max. 90 dagen).{' '}
        <strong className="text-text">Registratiebronnen</strong> en <strong className="text-text">dossier-splitsingen</strong>{' '}
        (regio, typologie) zijn <strong className="text-text">stand van nu</strong> voor alle gebruikers/projecten van de tenant —
        niet gefilterd op datum. Server-getrackte tool-calls (WOZ, subsidies, scenariovergelijking) tellen mee in de periode.
        Paginaweergaven alleen na analytics-cookie-opt-in. Alleen <strong className="text-text">platformbeheerders</strong>{' '}
        kunnen deze statistieken ophalen.
      </p>

      {isLoading && <p className="text-text-muted">Laden...</p>}
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {isForbidden
            ? 'Geen toegang: alleen platformbeheerders zien dit overzicht.'
            : `Kon statistieken niet laden. ${errMsg} Controleer of de migratie is uitgevoerd (pnpm db:migrate).`}
        </p>
      )}

      {data && (
        <>
          <p className="mb-4 text-xs text-text-muted">
            Rapportageperiode events/KPIs: <strong className="text-text">{data.period_days}</strong> dagen · tenant{' '}
            <span className="font-mono">{data.tenant_id}</span>
          </p>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <Eye className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Paginaweergaven (24u, live)</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.page_views_last_24h ?? '—'}</p>
              <p className="mt-1 text-xs text-text-muted">Unieke sessies: {data.unique_sessions_24h ?? '—'}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <Eye className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Paginaweergaven (periode)</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.page_views}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <FilePlus className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Projecten aangemaakt</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.project_created}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <FileCheck className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Offertes gegenereerd</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.offerte_generated}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <TrendingUp className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Conversie</span>
              </div>
              <p className="text-2xl font-bold text-primary">{data.conversion_pct}%</p>
              <p className="mt-1 text-xs text-text-muted">projecten / paginaweergaven</p>
            </div>
          </div>

          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-text">
            <Landmark className="h-5 w-5 text-primary" aria-hidden />
            Tool-gebruik (periode)
          </h2>
          <p className="mb-4 text-xs text-text-muted">
            Automatisch geregistreerd op de server bij een geslaagde API-call — geen afhankelijkheid van de cookiebanner.
            Scenariovergelijking toont o.a. CO₂-totalen; bruikbaar als <strong className="text-text">ESG/vergelijkings</strong>
            -indicator.
          </p>
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <Landmark className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">WOZ-ondersteuning</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.tool_woz_support}</p>
              <p className="mt-1 text-xs text-text-muted">POST /woz_support</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <PiggyBank className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Subsidies</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.tool_subsidies}</p>
              <p className="mt-1 text-xs text-text-muted">POST /calculate_subsidies</p>
            </div>
            <div className="rounded-lg border border-border bg-surface-card p-4">
              <div className="mb-2 flex items-center gap-2 text-text-muted">
                <GitCompare className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">Scenariovergelijking</span>
              </div>
              <p className="text-2xl font-bold text-text">{data.tool_compare_scenarios}</p>
              <p className="mt-1 text-xs text-text-muted">POST /compare_scenarios (o.a. CO₂)</p>
            </div>
          </div>

          {data.signup_source_breakdown.length > 0 && (
            <div className="mb-8 rounded-lg border border-border bg-surface-card p-5">
              <h2 className="mb-3 flex items-center gap-2 font-semibold text-text">
                <Users className="h-5 w-5 text-primary" aria-hidden />
                Registratiebron (alle gebruikers)
              </h2>
              <p className="mb-4 text-xs text-text-muted">
                Verdeling van <code className="rounded bg-surface px-1 text-xs">signup_source</code> bij accounts in deze
                tenant.
              </p>
              <ul className="divide-y divide-border text-sm">
                {data.signup_source_breakdown.map((row) => (
                  <li key={row.source_key} className="flex justify-between gap-4 py-2">
                    <span className="text-text">
                      <span className="font-medium">{row.label}</span>{' '}
                      <span className="font-mono text-xs text-text-muted">({row.source_key})</span>
                    </span>
                    <span className="shrink-0 font-medium text-primary">{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.projects_by_region.length > 0 && (
            <div className="mb-8 rounded-lg border border-border bg-surface-card p-5">
              <h2 className="mb-3 flex items-center gap-2 font-semibold text-text">
                <MapPin className="h-5 w-5 text-primary" aria-hidden />
                Projecten per regio (prijspeil)
              </h2>
              <p className="mb-4 text-xs text-text-muted">Gebaseerd op <code className="rounded bg-surface px-1 text-xs">context.region_id</code> in het dossier.</p>
              <ul className="divide-y divide-border text-sm">
                {data.projects_by_region.map((row) => (
                  <li key={row.region_id} className="flex justify-between gap-4 py-2">
                    <span className="text-text">{row.label}</span>
                    <span className="shrink-0 font-medium text-primary">{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.projects_by_project_type.length > 0 || data.projects_by_building_type.length > 0) && (
            <div className="mb-8 grid gap-4 lg:grid-cols-2">
              {data.projects_by_project_type.length > 0 && (
                <div className="rounded-lg border border-border bg-surface-card p-5">
                  <h2 className="mb-3 font-semibold text-text">Projecten per type</h2>
                  <ul className="divide-y divide-border text-sm">
                    {data.projects_by_project_type.map((row) => (
                      <li key={row.project_type} className="flex justify-between py-2">
                        <span className="text-text">{row.label}</span>
                        <span className="font-medium text-primary">{row.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.projects_by_building_type.length > 0 && (
                <div className="rounded-lg border border-border bg-surface-card p-5">
                  <h2 className="mb-3 font-semibold text-text">Projecten per gebouwtype</h2>
                  <ul className="divide-y divide-border text-sm">
                    {data.projects_by_building_type.map((row) => (
                      <li key={row.building_type} className="flex justify-between py-2">
                        <span className="text-text">{row.label}</span>
                        <span className="font-medium text-primary">{row.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {eventRows && eventRows.length > 0 && (
            <div className="mb-8 rounded-lg border border-border bg-surface-card p-5">
              <h2 className="mb-3 font-semibold text-text">Events (excl. page_view)</h2>
              <p className="mb-4 text-xs text-text-muted">
                Alle getelde event-types in deze periode — handig om nieuwe tracking te zien.
              </p>
              <ul className="divide-y divide-border text-sm">
                {eventRows.map(([type, count]) => (
                  <li key={type} className="flex justify-between py-2">
                    <span className="font-mono text-text">{type}</span>
                    <span className="font-medium text-primary">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.top_pages && data.top_pages.length > 0 && (
            <div className="rounded-lg border border-border bg-surface-card p-5">
              <h2 className="mb-4 font-semibold text-text">Meest bezochte pagina&apos;s</h2>
              <ul className="space-y-2">
                {data.top_pages.map((p, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-text">{p.path || '/'}</span>
                    <span className="font-medium text-primary">{p.count} weergaven</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
